import { useContext, useRef, type PointerEvent, type RefObject } from "react";
import { ComboBoxStateContext } from "react-aria-components";

interface ComboboxFieldBehaviorOptions {
  /**
   * Selector for field children that own their pointer interactions (beyond
   * the input and buttons, which are always excluded), e.g. chips.
   */
  interactiveSelector?: string;
  /**
   * Select the input's text when it takes focus any way other than a press on
   * the field. For single-select fields, where that text is the committed
   * value — never for a chips field, whose input holds an in-progress search
   * that react-aria refocuses mid-typing.
   */
  selectsValueOnFocus?: boolean;
}

/**
 * Shared pointer behavior for a combobox field ({@link ComboboxInput} and
 * {@link ComboboxChips}):
 *
 * - Clicking blank space in the field focuses the input, like a plain text
 *   field. The caret lands where it was clicked and the text is left alone.
 * - With `selectsValueOnFocus`, focus that arrives any other way — tabbing in,
 *   clicking the label — selects the value instead, ready to be replaced.
 *   Browsers do that for Tab already; a label click only focuses, so this is
 *   what makes the two routes agree.
 * - Pressing the control expands the popup (W3C combobox pattern). Tabbing into
 *   it does not, and neither does clicking the field's label — the browser
 *   forwards that click to the input, but it is a request to focus the field,
 *   not to open it. Disabled and read-only fields never expand, mirroring
 *   react-aria's own trigger.
 */
export function useComboboxFieldBehavior(
  inputRef: RefObject<HTMLInputElement | null>,
  options?: ComboboxFieldBehaviorOptions,
) {
  const state = useContext(ComboBoxStateContext);
  const ignoreSelector = (base: string) =>
    [base, options?.interactiveSelector].filter(Boolean).join(", ");

  // Set while a press that started on the field is still in flight, which is
  // what tells a click on the control from one the browser forwarded to it.
  const isPressed = useRef(false);

  const handleFieldPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const input = inputRef.current;
    if (input?.disabled) {
      return;
    }

    isPressed.current = true;
    const target = event.target as HTMLElement;

    // The press opens the popup, not the click it becomes: the browser turns a
    // label click into a click on the input, and there is no press to forward,
    // so opening here can't be triggered by one. (Base UI's combobox opens on
    // `mousedown-only` for the same reason.) Buttons and chips run their own
    // press handling.
    if (
      state &&
      !state.isOpen &&
      !input?.readOnly &&
      !target.closest(ignoreSelector("button"))
    ) {
      state.open(null, "manual");
    }

    if (target.closest(ignoreSelector("input, button"))) {
      return;
    }

    event.preventDefault();
    input?.focus();
  };

  const handleInputFocus = () => {
    const input = inputRef.current;
    if (!options?.selectsValueOnFocus || isPressed.current || !input) {
      return;
    }

    // Only when the field is at rest. react-aria refocuses the input while the
    // popup is open — measured at eight times over a five-character search, as
    // its option list changes — and selecting on one of those would let the
    // next keystroke wipe what the user typed. Focus that arrives at a closed
    // popup is someone coming to the field; nothing else is.
    //
    // (`relatedTarget` can't tell the two apart: it is null both for a label
    // click and for every one of those refocuses.)
    if (state?.isOpen) {
      return;
    }

    input.select();
  };

  // The press is over: a later focus is someone arriving at the field, not the
  // press that is already here.
  const handleFieldPointerUp = () => {
    isPressed.current = false;
  };

  return {
    handleFieldPointerDown,
    handleFieldPointerUp,
    handleInputFocus,
  };
}
